/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, EMPTY_IMMUTABLE_MAP, STAR } from '@lacele/const'
import { Factory } from '@lacele/utils'
import Reducer from '../Reducer'
import * as Types from '../types'
import { targetToKeyPath } from '../selector'
import { reducerAdded, reducerRemoved, removeState } from '../action'

export const defaultState = EMPTY_IMMUTABLE_MAP

export default class DynamicReducer {

    constructor() {
        this.globalReducers = {}
        this.targetReducers = {}
        this.reducer = this.reducer.bind(this)
    }

    // Installs this reducer to manage the whole or portion of the global state.
    // Returns this reducer function.
    install(rootKeyPath) {
        this.rootKeyPath = rootKeyPath || EMPTY_ARRAY
        return this.reducer
    }

    getRootKeyPath() {
        return this.rootKeyPath
    }

    // Should be called by a DynamicReducerProvider
    setDispatch(dispatch) {
        this.dispatch = dispatch
    }

    addTarget(storeTarget, reducerArg) {
        return this.addToContainer(this.targetReducers, storeTarget, reducerArg)
    }

    removeTarget(storeTarget, keepState) {
        return this.removeFromContainer(this.targetReducers, storeTarget, keepState)
    }

    addGlobal(reducerArg, reducerKey) {
        const storeTarget = reducerArg.name || reducerKey
        return this.addToContainer(this.globalReducers, storeTarget, reducerArg)
    }

    removeGlobal(reducerKey) {
        const storeTarget = reducerKey.name || reducerKey
        return this.removeFromContainer(this.globalReducers, storeTarget, true)
    }

    // Private

    // Logging

    logWarn(msg) {
        if (console) {
            if (console.warn) {
                console.warn(msg)
            } else if (console.log) {
                console.log(`WARNING: ${msg}`)
            }
        }
    }

    // Container

    addToContainer(container, storeTarget, reducerArg) {
        const reducerObj = {
            storeTarget
        }
        if (container === this.targetReducers) {
            reducerObj.storeKeyPath = targetToKeyPath(storeTarget, this.getRootKeyPath())
        }
        const prevReducerObj = container[storeTarget]
        if (prevReducerObj) {
            this.logWarn(`Target reducer already added at ${storeTarget}`)
            return false
        }
        container[storeTarget] = reducerObj
        this.onReducerWillMount(storeTarget, reducerArg, reducerObj)
        if (this.dispatch) {
            this.dispatch(reducerAdded(storeTarget, container === this.globalReducers))
        }
        this.onReducerDidMount(reducerObj)
    }

    removeFromContainer(container, storeTarget, keepState = false) {
        const reducerObj = container[storeTarget]
        if (reducerObj) {
            this.onReducerWillUnmount(reducerObj)
            if (this.dispatch) {
                this.dispatch(reducerRemoved(storeTarget, container === this.globalReducers))
            }
            delete container[storeTarget]
        }
        if (!keepState) {
            this.dispatch(removeState(storeTarget))
        }
    }

    // Lifecycle

    onReducerWillMount(storeTarget, reducerArg, reducerObj) {
        if (reducerArg instanceof Factory) {
            reducerObj.reducerInstance = reducerArg.getObject()
            reducerObj.reducerInstance.reducerWillMount(this, storeTarget)
            reducerObj.reducer = reducerObj.reducerInstance.reducer
        } else {
            reducerObj.reducer = reducerArg
        }
    }

    onReducerDidMount(reducerObj) {
        if (reducerObj.reducerInstance instanceof Reducer) {
            reducerObj.reducerInstance.reducerDidMount()
        }
    }

    onReducerWillUnmount(reducerObj) {
        if (reducerObj.reducerInstance instanceof Reducer) {
            reducerObj.reducerInstance.reducerWillUnmount()
        }
    }

    // Reducers

    reducer(state, action) {
        state = state || defaultState
        return state.withMutations((state) => {
            state = this.ownReducer(state, action)
            state = this.targetReducer(state, action)
            return this.globalReducer(state, action)
        })
    }

    ownReducer(state, action) {
        if (action.type === Types.REDUX_STATE_REMOVE
            && action.payload && action.payload.storeTarget) {
            const reducerEntry = this.targetReducers[action.payload.storeTarget]
            const storeKeyPath = reducerEntry ? reducerEntry.storeKeyPath
                : targetToKeyPath(action.payload.storeTarget, this.getRootKeyPath())
            if (storeKeyPath) {
                return state.removeIn(storeKeyPath)
            }
        }
        return state
    }

    targetReducer(state, action) {
        if (action.meta && action.meta.storeTarget && action.meta.storeTarget !== STAR) {
            // The action has a target.
            const reducerEntry = this.targetReducers[action.meta.storeTarget]
            if (reducerEntry) {
                // The action is routed to its target reducer.
                return this.targetReducerItem(state, action, reducerEntry)
            }
            // The action is not handled by any target reducer.
            return state
        }
        // The action is broadcast to all reducers.
        return Object.keys(this.targetReducers).reduce((state, storeTarget) => (
            this.targetReducerItem(state, action, this.targetReducers[storeTarget])
        ), state)
    }

    targetReducerItem(state, action, {
        storeKeyPath,
        reducer
    }) {
        return state.setIn(storeKeyPath, reducer(state.getIn(storeKeyPath), action))
    }

    globalReducer(state, action) {
        return Object.keys(this.globalReducers).reduce((state, reducerKey) => {
            const {
                reducer
            } = this.globalReducers[reducerKey]
            return reducer(state, action)
        }, state)
    }
}
